import { forwardRef } from 'react';
import { Radio } from 'antd';
import clsx from 'clsx';
import styles from './Option.module.css';

/**
 * @typedef {Object} OptionOwnProps
 * @property {import('react').ReactNode} label - Leading badge content (e.g. a letter).
 * @property {'correct' | 'incorrect'} [state] - Overrides the row/badge background to the
 *   success/error feedback tokens — e.g. after checking a quiz answer. Independent of `checked`:
 *   an unselected option can still be marked `correct` (to reveal the right answer), and a
 *   selected-but-wrong option is `incorrect` regardless of its `checked` styling.
 * @property {import('react').ReactNode} children - Main option content.
 */

/** @typedef {OptionOwnProps & Omit<import('antd').RadioProps, 'children'>} OptionProps */

/**
 * A single choice in a custom radio group (e.g. a quiz answer). A thin wrapper around antd's own
 * `Radio.Button` — not a from-scratch control — specifically so this gets antd's real `Form.Item`
 * wiring, native `<input type="radio">` semantics (arrow-key roving focus between grouped radios,
 * screen reader radio role, real form participation), and `RadioGroupContext` integration for
 * free, instead of us re-deriving an approximation of all of that by hand. The tradeoff is heavy
 * `:global()` CSS overrides in `Option.module.css` to reskin antd's default button-group look
 * into this row-with-a-circular-badge design — see that file for why `!important` shows up there.
 * Pair with `Option.Group` (a thin `Radio.Group` wrapper) for the actual group/selection state.
 *
 * @param {OptionProps} props
 */
const Option = forwardRef(({ label, state, className, children, ...rest }, ref) => (
    <Radio.Button
        ref={ref}
        className={clsx(
            styles.vecOption,
            state === 'correct' && styles.vecOptionCorrect,
            state === 'incorrect' && styles.vecOptionIncorrect,
            className,
        )}
        {...rest}
    >
        <span className={styles.vecOptionLead}>
            <span className={styles.vecOptionBadge}>{label}</span>
        </span>
        <span className={styles.vecOptionContent} data-vec-option-content>
            {children}
        </span>
    </Radio.Button>
));
Option.displayName = 'Option';

export default Option;
